import { useState } from "react";
import { REPORT_CATALOG, REPORT_FOOTER, type ReportId } from "@pms/shared";
import { API_BASE, downloadReport, useWorkspace } from "../api";
import { ErrorBox, Loading } from "../components/State";

export default function Reports() {
  const ws = useWorkspace();
  const [busy, setBusy] = useState<ReportId | null>(null);
  const [preview, setPreview] = useState<{ id: ReportId; text: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (ws.loading) return <Loading what="workspace" />;
  if (ws.error || !ws.data) return <ErrorBox error={ws.error ?? "no data"} what="workspace" />;

  const run = async (id: ReportId, mode: "download" | "preview") => {
    setBusy(id);
    setErr(null);
    try {
      if (mode === "download") {
        await downloadReport(id);
      } else {
        const res = await fetch(`${API_BASE}/v1/reports/${id}`, { method: "POST" });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        setPreview({ id, text: await res.text() });
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="card">
        <h2>Reports</h2>
        <p>
          Plain <code>.txt</code> built live from your workspace data. Drop one into Claude or Grok
          and ask for a board pack, an investor update or a spreadsheet.
        </p>
        <p className="mono-inline">Workspace: {ws.data.name}</p>
        {err && <p className="over">{err}</p>}
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Report</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {REPORT_CATALOG.map((r) => (
              <tr key={r.id}>
                <td>
                  <strong>{r.title}</strong>
                  <div className="mono-inline">{r.id}</div>
                </td>
                <td>
                  <div className="row">
                    <button
                      className="btn"
                      disabled={busy === r.id}
                      onClick={() => run(r.id, "preview")}
                    >
                      {busy === r.id ? "…" : "Preview"}
                    </button>
                    <button
                      className="btn primary"
                      disabled={busy === r.id}
                      onClick={() => run(r.id, "download")}
                    >
                      Download .txt
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="card">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 style={{ margin: 0 }}>{preview.id}</h2>
            <button className="btn" onClick={() => setPreview(null)}>
              Close
            </button>
          </div>
          <pre
            className="mono"
            style={{
              marginTop: 12,
              maxHeight: 520,
              overflow: "auto",
              whiteSpace: "pre",
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            {preview.text}
          </pre>
        </div>
      )}

      <div className="card">
        <h2>Format</h2>
        <p className="mono-inline" style={{ whiteSpace: "pre-wrap" }}>
          {REPORT_FOOTER}
        </p>
      </div>
    </>
  );
}
